import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  Area,
  AreaChart
} from "recharts";
import { TrendingUp, PieChart as PieChartIcon, BarChart3, Calendar as CalendarIcon, Clock } from "lucide-react";
import { Skeleton } from "./ui/skeleton";
import { Calendar } from "./ui/calendar";
import { it } from "date-fns/locale";

const COLORS = {
  ORTOPEDIA: "#3b82f6",
  FINANZIARIO: "#10b981", 
  CAF: "#f59e0b",
  PATRONATO: "#8b5cf6",
  LEGALE: "#ef4444",
  ASSICURAZIONE: "#06b6d4",
  LUCE_E_GAS: "#f97316"
};

const STATUS_COLORS = {
  richiesta: "#f59e0b",
  pronto: "#3b82f6",
  consegnato: "#10b981",
  non_accolta: "#ec4899",
  respinto: "#ef4444"
};

const STATUS_LABELS = {
  richiesta: "In Richiesta",
  pronto: "Pronti",
  consegnato: "Consegnati",
  non_accolta: "Non Accolta",
  respinto: "Respinti"
};

const AREA_LABELS = {
  ORTOPEDIA: "Ortopedia",
  FINANZIARIO: "Finanz.",
  CAF: "CAF",
  PATRONATO: "Patronato",
  LEGALE: "Legale",
  ASSICURAZIONE: "Assicuraz.",
  LUCE_E_GAS: "Luce&Gas"
};

const DashboardCharts = ({ className = "" }) => {
  const [loading, setLoading] = useState(true);
  const [statsByArea, setStatsByArea] = useState({});
  const [generalStats, setGeneralStats] = useState({});
  const [activeChart, setActiveChart] = useState("bar");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    fetchStats();
    // Update time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const [areaRes, generalRes] = await Promise.all([
        axios.get(`${API}/stats/by-area`, { withCredentials: true }),
        axios.get(`${API}/stats`, { withCredentials: true })
      ]);
      setStatsByArea(areaRes.data);
      setGeneralStats(generalRes.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  // Transform data for bar chart (orders by area)
  const barChartData = Object.entries(statsByArea).map(([area, stats]) => ({
    name: AREA_LABELS[area] || area,
    totale: stats.totale,
    richiesta: stats.richiesta,
    pronto: stats.pronto,
    consegnato: stats.consegnato,
    fill: COLORS[area] || "#64748b"
  })).filter(item => item.totale > 0);

  // Transform data for pie chart (status distribution)
  const pieChartData = [
    { name: STATUS_LABELS.richiesta, value: generalStats.richiesta || 0, color: STATUS_COLORS.richiesta },
    { name: STATUS_LABELS.pronto, value: generalStats.pronto || 0, color: STATUS_COLORS.pronto },
    { name: STATUS_LABELS.consegnato, value: generalStats.consegnato || 0, color: STATUS_COLORS.consegnato },
    { name: STATUS_LABELS.non_accolta, value: generalStats.non_accolta || 0, color: STATUS_COLORS.non_accolta },
    { name: STATUS_LABELS.respinto, value: generalStats.respinto || 0, color: STATUS_COLORS.respinto }
  ].filter(item => item.value > 0);

  // Area distribution pie chart
  const areaPieData = Object.entries(statsByArea).map(([area, stats]) => ({
    name: AREA_LABELS[area] || area,
    value: stats.totale,
    color: COLORS[area] || "#64748b"
  })).filter(item => item.value > 0);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/95 backdrop-blur-sm border border-slate-200 rounded-xl shadow-lg p-3">
          <p className="font-semibold text-slate-900 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color || entry.fill }}>
              {entry.name}: <span className="font-medium">{entry.value}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${className}`}>
        <Skeleton className="h-80 rounded-2xl" />
        <Skeleton className="h-80 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`} data-testid="dashboard-charts">
      {/* Chart Type Selector */}
      <div className="flex gap-2 justify-center">
        <button
          onClick={() => setActiveChart("bar")}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
            activeChart === "bar" 
              ? "bg-slate-900 text-white shadow-lg" 
              : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
          }`}
          data-testid="chart-type-bar"
        >
          <BarChart3 className="w-4 h-4" />
          Per Area
        </button>
        <button
          onClick={() => setActiveChart("pie")}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
            activeChart === "pie" 
              ? "bg-slate-900 text-white shadow-lg" 
              : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
          }`}
          data-testid="chart-type-pie"
        >
          <PieChartIcon className="w-4 h-4" />
          Per Stato
        </button>
        <button
          onClick={() => setActiveChart("area")}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
            activeChart === "area" 
              ? "bg-slate-900 text-white shadow-lg" 
              : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
          }`}
          data-testid="chart-type-area"
        >
          <TrendingUp className="w-4 h-4" />
          Distribuzione
        </button>
      </div>

      {/* Charts Container */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Main Chart */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            {activeChart === "bar" && <><BarChart3 className="w-5 h-5 text-blue-500" /> Ordini per Macro Area</>}
            {activeChart === "pie" && <><PieChartIcon className="w-5 h-5 text-purple-500" /> Distribuzione Stati</>}
            {activeChart === "area" && <><TrendingUp className="w-5 h-5 text-emerald-500" /> Distribuzione Aree</>}
          </h3>
          
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              {activeChart === "bar" ? (
                <BarChart data={barChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fill: '#64748b', fontSize: 11 }}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis 
                    tick={{ fill: '#64748b', fontSize: 11 }}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="totale" 
                    name="Totale"
                    radius={[8, 8, 0, 0]}
                  >
                    {barChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              ) : activeChart === "pie" ? (
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              ) : (
                <PieChart>
                  <Pie
                    data={areaPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {areaPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    layout="horizontal" 
                    verticalAlign="bottom"
                    formatter={(value) => <span className="text-xs text-slate-600">{value}</span>}
                  />
                </PieChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        {/* Calendar with Time */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-amber-500" />
            Calendario
          </h3>
          
          <div className="flex flex-col items-center">
            {/* Digital Clock */}
            <div className="mb-4 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl px-6 py-3 shadow-lg">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-blue-400" />
                <div className="text-2xl font-mono font-bold text-white tracking-wider">
                  {currentTime.toLocaleTimeString("it-IT", { 
                    hour: "2-digit", 
                    minute: "2-digit",
                    second: "2-digit"
                  })}
                </div>
              </div>
              <div className="text-center text-xs text-slate-400 mt-1">
                {currentTime.toLocaleDateString("it-IT", { 
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric"
                })}
              </div>
            </div>
            
            {/* Calendar */}
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              locale={it}
              className="rounded-xl border border-slate-200"
              classNames={{
                months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                month: "space-y-4",
                caption: "flex justify-center pt-1 relative items-center",
                caption_label: "text-sm font-semibold text-slate-800",
                nav: "space-x-1 flex items-center",
                nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 hover:bg-slate-100 rounded-lg",
                nav_button_previous: "absolute left-1",
                nav_button_next: "absolute right-1",
                table: "w-full border-collapse space-y-1",
                head_row: "flex",
                head_cell: "text-slate-500 rounded-md w-9 font-medium text-[0.8rem]",
                row: "flex w-full mt-2",
                cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-blue-50 rounded-lg",
                day: "h-9 w-9 p-0 font-normal hover:bg-slate-100 rounded-lg transition-colors",
                day_selected: "bg-blue-500 text-white hover:bg-blue-600 hover:text-white focus:bg-blue-500 focus:text-white rounded-lg",
                day_today: "bg-amber-100 text-amber-900 font-semibold",
                day_outside: "text-slate-300",
                day_disabled: "text-slate-300 opacity-50",
                day_hidden: "invisible",
              }}
            />
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-4 text-white">
          <div className="text-3xl font-bold">{generalStats.totale || 0}</div>
          <div className="text-sm opacity-80">Totale Ordini</div>
        </div>
        <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl p-4 text-white">
          <div className="text-3xl font-bold">{generalStats.richiesta || 0}</div>
          <div className="text-sm opacity-80">In Lavorazione</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-400 to-green-500 rounded-2xl p-4 text-white">
          <div className="text-3xl font-bold">{generalStats.consegnato || 0}</div>
          <div className="text-sm opacity-80">Completati</div>
        </div>
        <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl p-4 text-white">
          <div className="text-3xl font-bold">{generalStats.pagato || 0}</div>
          <div className="text-sm opacity-80">Pagati</div>
        </div>
      </div>
    </div>
  );
};

export default DashboardCharts;
